import { motion } from 'framer-motion'
import { useBrowseServices } from '../../../hooks/queries/useBrowseServices'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { ServicePurchaseModal } from '../../../components/services/service-purchase-modal'
import { useState } from 'react'
import type { Service } from '../../../models/service'
import { CheckCircle, Star, Zap, Shield, ArrowRight, ShoppingCart } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import { useDesignSystem } from '@/hooks/useDesignSystem'
import { LoadingSpinner } from '@/components/ui/loading'

const CYAN_THEME_COLORS = [
  ['#0dcbfc', '#0891b2'],
  ['#22d3ee', '#155e75'],
  ['#06b6d4', '#164e63'],
  ['#67e8f9', '#0dcbfc'],
  ['#0891b2', '#06b6d4'],
  ['#155e75', '#22d3ee'],
  ['#0dcbfc', '#67e8f9'],
  ['#164e63', '#0891b2'],
]

function getSeededGradient(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  const index = Math.abs(hash) % CYAN_THEME_COLORS.length
  const [color1, color2] = CYAN_THEME_COLORS[index]
  return `linear-gradient(135deg, ${color1}22 0%, ${color2}15 50%, ${color1}10 100%)`
}

const getServiceIcon = (name: string) => {
  if (name.toLowerCase().includes('basic')) return <Zap className="h-6 w-6" />
  if (name.toLowerCase().includes('standard')) return <Star className="h-6 w-6" />
  if (name.toLowerCase().includes('premium')) return <CheckCircle className="h-6 w-6" />
  if (name.toLowerCase().includes('enterprise')) return <Shield className="h-6 w-6" />
  return <Star className="h-6 w-6" />
}

export default function BrowseServicesPage() {
  const { t } = useTranslation()
  const { services } = useBrowseServices()
  const { animations, patterns } = useDesignSystem()
  const [selectedService, setSelectedService] = useState<Service | null>(null)
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false)

  const handlePurchase = (service: Service) => {
    setSelectedService(service)
    setIsPurchaseModalOpen(true)
  }

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false)
    setSelectedService(null)
  }

  if (services.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <LoadingSpinner size="lg" text={t('pages.services.loadingAvailable')} />
      </div>
    )
  }

  if (services.error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
          <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="text-center">
          <h3 className={'text-lg font-semibold mb-2 ' + patterns.text.heading}>
            {t('pages.services.loadError')}
          </h3>
          <p className={patterns.text.muted + ' mb-4'}>
            {t('pages.services.loadAvailableError')}
          </p>
          <Button 
            onClick={() => services.refetch()} 
            variant="outline"
            className="bg-white hover:bg-muted dark:bg-card dark:hover:bg-muted"
          >
            {t('common.buttons.refresh')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <motion.div 
      variants={animations.component.staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      key="browse-services-page"
    >
      {/* Header Section */}
      <motion.div variants={animations.content.header} className="text-center space-y-4">
        <div className={'inline-flex items-center px-4 py-2 border border-border bg-muted/50 ' + patterns.radius.full}>
          <span className="text-sm font-medium text-primary">
            {t('pages.services.browsePurchase')}
          </span>
        </div>
        <h1 className={'text-4xl lg:text-5xl font-bold ' + patterns.text.heading}>
          {t('pages.services.availableServices')}
        </h1>
        <p className={'text-lg max-w-2xl mx-auto ' + patterns.text.muted}>
          {t('pages.services.browseSubtitle')}
        </p>
      </motion.div>

      {/* Services Grid */}
      <motion.div 
        variants={animations.component.staggerContainer}
        initial="hidden"
        animate="visible"
        className={patterns.layout.cardGrid}
        key="browse-services-grid"
      >
        {services.data?.map((service) => (
          <motion.div 
            key={`browse-${service.id}`}
            variants={animations.content.gridItem}
            whileHover={{ y: -4, transition: { duration: 0.15 } }}
            className="group"
          >
            <Card className={'h-full overflow-hidden ' + patterns.card.elevated}>
              {/* Card Header with Gradient Background */}
              <div 
                className="h-24 relative overflow-hidden"
                style={{ background: getSeededGradient(service.name) }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#080c15]/30 to-transparent"></div>
                <div className="absolute top-4 left-4 text-primary">
                  {getServiceIcon(service.name)}
                </div>
                <div className="absolute top-4 right-4">
                  <div className={'px-2 py-1 rounded-full text-xs font-medium ' + 
                    (service.status === 'active' ? patterns.status.success : patterns.status.warning)
                  }>
                    {service.status === 'active' ? t('pages.services.available') : t('pages.services.comingSoon')}
                  </div>
                </div>
              </div>

              <CardHeader className="pb-2">
                <CardTitle className={'text-xl font-bold group-hover:text-primary transition-colors ' + patterns.text.heading}>
                  {service.name}
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4 flex-1 flex flex-col">
                <div 
                  className={'text-sm leading-relaxed flex-1 line-clamp-2 ' + patterns.text.muted}
                  dangerouslySetInnerHTML={{ __html: service.description }}
                />
                
                <div className="space-y-4">
                  {/* Price */}
                  <div className="flex items-baseline gap-2">
                    <span className={'text-3xl font-bold ' + patterns.text.heading}>
                      ${service.price}
                    </span>
                    <span className={patterns.text.muted}>
                      {t('pages.services.oneTime')}
                    </span>
                  </div>

                  {/* Action Button */}
                  <Button 
                    className={'w-full group/btn transition-all duration-300 ' +
                      (service.status !== 'active'
                        ? 'bg-muted-foreground/50 hover:bg-muted-foreground text-white cursor-not-allowed'
                        : 'bg-primary hover:bg-primary/90 text-primary-foreground hover:shadow-lg transform hover:scale-[1.02]')
                    }
                    disabled={service.status !== 'active'}
                    onClick={() => handlePurchase(service)}
                  >
                    <span className="flex items-center gap-2">
                      {service.status !== 'active' ? (
                        <>
                          <CheckCircle size={16} />
                          {t('pages.services.comingSoon')}
                        </>
                      ) : (
                        <>
                          <ShoppingCart size={16} />
                          {t('pages.services.purchaseNow')}
                          <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                        </>
                      )}
                    </span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Empty State */}
      {services.data && services.data.length === 0 && (
        <motion.div 
          variants={animations.content.header}
          className="text-center py-16"
        >
          <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-muted dark:bg-muted flex items-center justify-center">
            <ShoppingCart className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className={'text-xl font-semibold mb-2 ' + patterns.text.heading}>
            {t('pages.services.noServices')}
          </h3>
          <p className={patterns.text.muted}>
            {t('pages.services.noServicesMessage')}
          </p>
        </motion.div>
      )}

      <ServicePurchaseModal 
        isOpen={isPurchaseModalOpen}
        onClose={closePurchaseModal}
        service={selectedService}
      />
    </motion.div>
  )
}
